/**
 * Renderers for the teachable-lesson blocks (curriculum §3).
 *
 * These carry the depth a teacher teaches from: objectives, the line-by-line
 * gloss, the grouped Wortschatz with article+plural, pronunciation, culture,
 * translation homework, the weighted test, and the review recall prompts.
 *
 * Bilingual by construction: explanatory prose arrives as {en, de}; `Bi`
 * renders the side matching the app's current locale, falling back to the
 * other if one is missing (defensive — the importer forbids that shape, but
 * a renderer must never blank out).
 *
 * All follow the activities.tsx contract: call `onDone(null)` when finished
 * for informational blocks; scored blocks (lesson_test) report true/false.
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useI18n, useT } from "../../i18n";
import { speak } from "../../utils/tts";
import type { ActivityProps } from "./activities";

// ---------------------------------------------------------------------------
// Bilingual helpers
// ---------------------------------------------------------------------------
export interface Localised {
  en?: string;
  de?: string;
}

/** Pick the localised side for the current UI locale, with a safe fallback. */
export function pick(v: Localised | string | undefined, locale: "en" | "de"): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return (locale === "de" ? v.de : v.en) ?? v.en ?? v.de ?? "";
}

/** Inline bilingual text bound to the current locale. */
function Bi({ v }: { v: Localised | string | undefined }) {
  const { locale } = useI18n();
  return <>{pick(v, locale)}</>;
}

function Listen({ text }: { text: string }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => speak(text)}
      aria-label={`${t("player.listen")}: ${text}`}
      className="text-brand hover:text-brand-dark"
    >
      🔊
    </button>
  );
}

/** Continue button shared by the informational blocks. */
function Done({ onDone }: { onDone: ActivityProps["onDone"] }) {
  const t = useT();
  return (
    <Button onClick={() => onDone(null)} className="mt-5">
      {t("player.continue")}
    </Button>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-brand-dark">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// 1 · objectives (Lernziele)
// ---------------------------------------------------------------------------
function Objectives({ payload, onDone }: ActivityProps) {
  const t = useT();
  const items = (payload.items as Localised[] | undefined) ?? [];
  return (
    <div>
      <div className="rounded-card border border-brand/30 bg-brand-soft p-4">
        <SectionLabel>{t("learn.objectives")}</SectionLabel>
        <ul className="mt-3 space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex gap-2 text-ink">
              <span aria-hidden className="text-brand">✓</span>
              <span><Bi v={it} /></span>
            </li>
          ))}
        </ul>
      </div>
      <Done onDone={onDone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2 · line_gloss (Satz für Satz) — the teacher's voice
// ---------------------------------------------------------------------------
interface GlossLine {
  de: string;
  speaker?: string;
  literal: string;
  translation: string;
  note: Localised;
}

function LineGloss({ payload, onDone }: ActivityProps) {
  const t = useT();
  const lines = (payload.lines as GlossLine[] | undefined) ?? [];
  return (
    <div>
      <SectionLabel>{t("learn.lineGloss")}</SectionLabel>
      <ol className="mt-3 space-y-4">
        {lines.map((ln, i) => (
          <li key={i} className="rounded-card border border-line bg-surface p-4">
            <p className="flex items-center gap-2 font-semibold text-ink">
              {ln.speaker && <span className="text-muted">{ln.speaker}:</span>}
              <span>{ln.de}</span>
              <Listen text={ln.de} />
            </p>
            <p className="mt-1 text-sm italic text-muted">{ln.literal}</p>
            <p className="mt-1 text-sm text-ink">{ln.translation}</p>
            <p className="mt-2 border-l-2 border-brand/40 pl-3 text-sm text-ink">
              <Bi v={ln.note} />
            </p>
          </li>
        ))}
      </ol>
      <Done onDone={onDone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3 · word_bank / extra_words (Wortschatz / Andere Wörter)
// ---------------------------------------------------------------------------
interface Word {
  de: string;
  gloss: Localised;
  article?: string;
  plural?: string;
  stress?: string;
}
interface WordGroup {
  kind: string;
  label: Localised;
  words: Word[];
}

/** Render "**Biolo**gie" style stress marking as bold on the marked syllable. */
function withStress(word: string, stress?: string): ReactNode {
  if (!stress) return word;
  const parts = stress.split(/\*\*(.+?)\*\*/); // ["Biolo", "gie", ""]
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>));
}

function WordBank({ payload, onDone }: ActivityProps) {
  const t = useT();
  const groups = (payload.groups as WordGroup[] | undefined) ?? [];
  return (
    <div>
      <SectionLabel>{t("learn.wordBank")}</SectionLabel>
      <div className="mt-3 space-y-5">
        {groups.map((g, gi) => (
          <div key={gi}>
            <p className="text-sm font-bold text-ink"><Bi v={g.label} /></p>
            <div className="overflow-x-auto">
              <table className="mt-2 w-full text-sm">
                <tbody>
                  {g.words.map((w, wi) => (
                    <tr key={wi} className="border-b border-line/60">
                      <td className="py-1.5 pr-3 font-medium text-ink">
                        {w.article && <span className="text-muted">{w.article} </span>}
                        {withStress(w.de, w.stress)}
                        {w.plural && <span className="text-muted">, {w.plural}</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-ink"><Bi v={w.gloss} /></td>
                      <td className="w-8 py-1.5 text-right"><Listen text={w.de} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      <Done onDone={onDone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4 · pronunciation (Aussprache)
// ---------------------------------------------------------------------------
function Pronunciation({ payload, onDone }: ActivityProps) {
  const t = useT();
  const focus = String(payload.focus ?? "");
  const pairs = (payload.minimal_pairs as { a: string; b: string }[] | undefined) ?? [];
  const examples = (payload.examples as string[] | undefined) ?? [];
  return (
    <div>
      <div className="rounded-card border border-bronze/40 bg-surface p-4">
        <SectionLabel>{t("learn.pronunciation")}</SectionLabel>
        <div className="mt-2 text-3xl font-extrabold text-ink">
          <TapToTranslate block text={focus} />
        </div>
        <p className="mt-2 text-ink"><Bi v={payload.instruction as Localised} /></p>
        {pairs.length > 0 && (
          <div className="mt-3">
            <p className="text-xs uppercase text-muted">{t("learn.minimalPairs")}</p>
            <ul className="mt-1 space-y-1">
              {pairs.map((p, i) => (
                <li key={i} className="flex items-center gap-3 text-ink">
                  <span>{p.a}</span><Listen text={p.a} />
                  <span className="text-muted">·</span>
                  <span>{p.b}</span><Listen text={p.b} />
                </li>
              ))}
            </ul>
          </div>
        )}
        {examples.length > 0 && (
          <p className="mt-3 flex flex-wrap gap-2">
            {examples.map((e, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-control bg-canvas px-2 py-1 text-sm">
                {e} <Listen text={e} />
              </span>
            ))}
          </p>
        )}
      </div>
      <Done onDone={onDone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5 · culture_note (Landeskunde)
// ---------------------------------------------------------------------------
function CultureNote({ payload, onDone }: ActivityProps) {
  const t = useT();
  return (
    <div>
      <div className="rounded-card border border-brand/30 bg-brand-soft p-4">
        <SectionLabel>
          {t("learn.culture")}
          {payload.city != null && <span className="ml-2 normal-case text-muted">· {String(payload.city)}</span>}
        </SectionLabel>
        <p className="mt-2 text-lg font-bold text-ink"><Bi v={payload.title as Localised} /></p>
        <p className="mt-2 whitespace-pre-line text-ink"><Bi v={payload.body as Localised} /></p>
      </div>
      <Done onDone={onDone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6 · translation_set (Übersetzung) — self-checked homework
// ---------------------------------------------------------------------------
interface TransItem {
  prompt: Localised;
  answer: string;
  alternatives?: string[];
}

function TranslationSet({ payload, onDone }: ActivityProps) {
  const t = useT();
  const items = (payload.items as TransItem[] | undefined) ?? [];
  const [answers, setAnswers] = useState<string[]>(() => items.map(() => ""));
  const [revealed, setRevealed] = useState(false);

  return (
    <div>
      <SectionLabel>{t("learn.translation")}</SectionLabel>
      <p className="mt-1 text-sm text-muted">{t("learn.translationHint")}</p>
      <ol className="mt-3 space-y-3">
        {items.map((it, i) => (
          <li key={i}>
            <p className="text-ink">
              <span className="mr-2 text-muted">{i + 1}.</span>
              <Bi v={it.prompt} />
            </p>
            <input
              value={answers[i]}
              onChange={(e) =>
                setAnswers((prev) => prev.map((a, j) => (j === i ? e.target.value : a)))
              }
              disabled={revealed}
              aria-label={`${t("learn.translation")} ${i + 1}`}
              className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink disabled:opacity-60"
            />
            {revealed && (
              <p className="mt-1 text-sm">
                <span className="text-success">✓ {it.answer}</span>
                <span className="ml-2 align-middle"><Listen text={it.answer} /></span>
              </p>
            )}
          </li>
        ))}
      </ol>
      {!revealed ? (
        <Button onClick={() => setRevealed(true)} className="mt-4">
          {t("learn.showAnswers")}
        </Button>
      ) : (
        <Done onDone={onDone} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7 · lesson_test (Lernkontrolle) — weighted, auto-marked
// ---------------------------------------------------------------------------
interface TestItem {
  kind: "mc" | "gap" | "translate" | "match";
  prompt: Localised;
  options?: string[];
  answer: string;
}
interface TestSection {
  name: Localised;
  weight: number;
  items: TestItem[];
}

/** Normalise a free-text answer for lenient marking of gap/translate items. */
function norm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ").replace(/[.!?]+$/, "");
}

function LessonTest({ payload, onDone }: ActivityProps) {
  const t = useT();
  const { locale } = useI18n();
  const sections = (payload.sections as TestSection[] | undefined) ?? [];
  const flat = useMemo(
    () => sections.flatMap((s) => s.items.map((it) => ({ it, weight: s.weight, count: s.items.length }))),
    [sections],
  );
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [graded, setGraded] = useState(false);

  const score = useMemo(() => {
    if (!graded) return 0;
    let earned = 0;
    flat.forEach(({ it, weight, count }, i) => {
      const per = weight / count;
      const given = answers[i] ?? "";
      const ok =
        it.kind === "mc"
          ? given === it.answer
          : norm(given) === norm(it.answer);
      if (ok) earned += per;
    });
    return Math.round(earned);
  }, [graded, flat, answers]);

  const setA = (i: number, v: string) => setAnswers((p) => ({ ...p, [i]: v }));
  let idx = -1;

  return (
    <div>
      <SectionLabel>{t("learn.lessonTest")}</SectionLabel>
      <div className="mt-3 space-y-6">
        {sections.map((sec, si) => (
          <div key={si}>
            <p className="flex flex-wrap items-baseline justify-between gap-2 text-sm font-bold text-ink">
              <span><Bi v={sec.name} /></span>
              <span className="text-muted">{sec.weight} {t("learn.points")}</span>
            </p>
            <ol className="mt-2 space-y-3">
              {sec.items.map((it) => {
                idx += 1;
                const i = idx;
                const given = answers[i] ?? "";
                const correct = it.kind === "mc" ? given === it.answer : norm(given) === norm(it.answer);
                return (
                  <li key={i}>
                    <p className="text-ink"><Bi v={it.prompt} /></p>
                    {it.kind === "mc" ? (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {(it.options ?? []).map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            disabled={graded}
                            onClick={() => setA(i, opt)}
                            aria-pressed={given === opt}
                            className={`rounded-control border px-3 py-1.5 text-sm ${
                              given === opt ? "border-brand bg-brand text-white" : "border-line text-ink hover:border-brand"
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        value={given}
                        disabled={graded}
                        onChange={(e) => setA(i, e.target.value)}
                        aria-label={`${pick(it.prompt, locale)}`}
                        className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink disabled:opacity-60"
                      />
                    )}
                    {graded && (
                      <p className={`mt-1 text-sm ${correct ? "text-success" : "text-danger"}`}>
                        {correct ? t("player.correct") : `${t("player.answerWas")} ${it.answer}`}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
      {!graded ? (
        <Button onClick={() => setGraded(true)} className="mt-5">
          {t("learn.submitTest")}
        </Button>
      ) : (
        <div className="mt-5">
          <p className="text-lg font-extrabold text-ink">
            {t("learn.yourScore")}: {score}%
          </p>
          <Button onClick={() => onDone(score >= 60)} className="mt-3">
            {t("player.continue")}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8 · recall_prompt (review — Können Sie sich erinnern?)
// ---------------------------------------------------------------------------
interface RecallItem {
  prompt: Localised;
  answer: Localised;
}

function RecallPrompt({ payload, onDone }: ActivityProps) {
  const t = useT();
  const prompts = (payload.prompts as RecallItem[] | undefined) ?? [];
  const [open, setOpen] = useState<Record<number, boolean>>({});
  return (
    <div>
      <SectionLabel>{t("learn.recall")}</SectionLabel>
      <ul className="mt-3 space-y-2">
        {prompts.map((p, i) => (
          <li key={i} className="rounded-card border border-line bg-surface p-3">
            <p className="font-medium text-ink"><Bi v={p.prompt} /></p>
            {open[i] ? (
              <p className="mt-2 border-l-2 border-brand/40 pl-3 text-sm text-ink">
                <Bi v={p.answer} />
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [i]: true }))}
                className="mt-1 text-sm font-semibold text-brand hover:text-brand-dark"
              >
                {t("learn.reveal")}
              </button>
            )}
          </li>
        ))}
      </ul>
      <Done onDone={onDone} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registry — merged into activities.tsx RENDERERS
// ---------------------------------------------------------------------------
export const LEARN_BLOCK_RENDERERS: Record<string, (p: ActivityProps) => JSX.Element> = {
  objectives: Objectives,
  line_gloss: LineGloss,
  word_bank: WordBank,
  extra_words: WordBank,
  pronunciation: Pronunciation,
  culture_note: CultureNote,
  translation_set: TranslationSet,
  lesson_test: LessonTest,
  recall_prompt: RecallPrompt,
};

/** lesson_test is the only new block that affects the score. */
export const LEARN_SCORED_TYPES = ["lesson_test"];
